import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, Code2, LineChart, Briefcase, Home, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WaitlistCTA } from './WaitlistCTA';
function SlackIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5006" data-mp="14lcpwsf6d7jm">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-5007" data-mp="1scrteoiotuwg" />
      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#36C5F0" data-id="element-5008" data-mp="1nki7djkp1ezg" />
      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-5009" data-mp="xfs44eefe1gf" />
      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-5010" data-mp="177en4wx094tq" />
    </svg>;
}
const FUNCTIONS = [{
  id: 'marketing',
  icon: Megaphone,
  name: 'Growth & Marketing',
  status: 'Live',
  desc: 'Campaign recaps, attribution, and spend monitoring.',
  scenario: {
    title: 'Consolidate data from multiple ad accounts',
    user: {
      name: 'Maya Chen',
      avatar: 'https://i.pravatar.cc/100?img=44',
      time: '11:37 AM',
      message: <>
            <span className="text-[#EA580C] bg-[#EA580C]/10 px-1 rounded" data-id="element-5011" data-mp="3myaohhif8zm9">
              @Giga
            </span>{' '}
            weekly recap for Q1 campaigns. Spend, ROAS, pipeline generated.
            Branded PDF in{' '}
            <span className="text-[#EA580C]" data-id="element-5012" data-mp="kivvuyydryx7">#marketing-leadership</span>.
          </>
    },
    giga: {
      time: '11:38 AM',
      message: <div className="space-y-3" data-id="element-5013" data-mp="ufigpnqpipz8">
            <p data-id="element-5014" data-mp="ugjsjufjrgi0">Done.</p>
            <ul className="space-y-1" data-id="element-5015" data-mp="ucw0z0tnx45x">
              <li data-id="element-5016" data-mp="zf4rlqj2bu4v">• Spend: $184k (Google $112k / LinkedIn $72k)</li>
              <li data-id="element-5017" data-mp="13fx3krjbkd4u">• Pipeline generated: $1.1M (6x ROAS)</li>
              <li data-id="element-5018" data-mp="13edkkt6a3nzd">• Closed-won revenue: $312k so far</li>
            </ul>
            <p data-id="element-5019" data-mp="187okeacob4hs">
              PDF in{' '}
              <span className="text-[#EA580C]" data-id="element-5020" data-mp="188q75c31xffc">#marketing-leadership</span>.
              Schedule every Monday?
            </p>
          </div>,
      reactions: [{
        emoji: '✅',
        count: 2
      }]
    }
  }
}, {
  id: 'sales',
  icon: Briefcase,
  name: 'Sales & Success',
  status: 'Live',
  desc: 'Meeting prep, CRM hygiene, and follow-ups.',
  scenario: {
    title: 'Prep for a high-stakes negotiation call',
    user: {
      name: 'Jordan Reyes',
      avatar: 'https://i.pravatar.cc/100?img=68',
      time: '2:14 PM',
      message: <>
            <span className="text-[#EA580C] bg-[#EA580C]/10 px-1 rounded" data-id="element-5021" data-mp="2xotvpvi0zvg9">
              @Giga
            </span>{' '}
            prep me for the Acme call tomorrow. Pull the deal stage, recent
            emails, and the latest proposal.
          </>
    },
    giga: {
      time: '2:15 PM',
      message: <div className="space-y-3" data-id="element-5022" data-mp="3leehem5kd6t8">
            <p data-id="element-5023" data-mp="3lfg2ljgan9oo">Done. Briefing ready:</p>
            <ul className="space-y-1" data-id="element-5024" data-mp="3gm4s98c9was0">
              <li data-id="element-5025" data-mp="3gl2252mrycd5">• Deal: $84k ARR (Negotiation stage)</li>
              <li data-id="element-5026" data-mp="3bo39koiredh2">• Room: Eunice (champion), Oleg (CFO)</li>
              <li data-id="element-5027" data-mp="3blh36s7lu40f">• Talking point: SOC 2 lands April 15</li>
            </ul>
            <p data-id="element-5028" data-mp="cz4i1lt26fp5">Want me to draft a 2-month pilot option for Oleg?</p>
          </div>,
      reactions: [{
        emoji: '🔥',
        count: 3
      }]
    }
  }
}, {
  id: 'engineering',
  icon: Code2,
  name: 'Engineering',
  status: 'Live',
  desc: 'Error triage, incident response, and PR reviews.',
  scenario: {
    title: 'Triage incoming production errors',
    user: {
      name: 'Sarah Chen',
      avatar: 'https://i.pravatar.cc/100?img=47',
      time: '9:02 AM',
      message: <>
            <span className="text-[#EA580C] bg-[#EA580C]/10 px-1 rounded" data-id="element-5029" data-mp="3jvmvyh9lhfdg">
              @Giga
            </span>{' '}
            triage the new Sentry errors from last night's deploy. Group by root
            cause and create Linear tickets.
          </>
    },
    giga: {
      time: '9:03 AM',
      message: <div className="space-y-3" data-id="element-5030" data-mp="1oyq2p6q0o6q">
            <p data-id="element-5031" data-mp="6ol3aymju4fj">Done. Triaged 14 errors into 3 tickets:</p>
            <ul className="space-y-1" data-id="element-5032" data-mp="6n0bvhvj71lw">
              <li data-id="element-5033" data-mp="ue5nfkufv7qe">• ENG-842: Auth token expiration edge case (High)</li>
              <li data-id="element-5034" data-mp="uc2qshys6gcp">• ENG-843: Missing translation keys in onboarding (Low)</li>
              <li data-id="element-5035" data-mp="z6gq2vgkd7mn">• ENG-844: Redis timeout on heavy exports (Med)</li>
            </ul>
            <p data-id="element-5036" data-mp="2ju9fie8mg5w2">Tickets created in Linear. Ping the on-call for ENG-842?</p>
          </div>,
      reactions: [{
        emoji: '🙌',
        count: 4
      }]
    }
  }
}, {
  id: 'finance',
  icon: LineChart,
  name: 'Finance & Ops',
  status: 'Live',
  desc: 'Reconciliation, vendor spend, and board metrics.',
  scenario: {
    title: 'Reconcile daily payouts to accounting',
    user: {
      name: 'Oleg Petrov',
      avatar: 'https://i.pravatar.cc/100?img=33',
      time: '8:30 AM',
      message: <>
            <span className="text-[#EA580C] bg-[#EA580C]/10 px-1 rounded" data-id="element-5037" data-mp="1vynel66um89h">
              @Giga
            </span>{' '}
            reconcile yesterday's Stripe payouts to Xero. Flag any mismatched
            amounts or missing invoices.
          </>
    },
    giga: {
      time: '8:32 AM',
      message: <div className="space-y-3" data-id="element-5038" data-mp="3layzo3nsk3hs">
            <p data-id="element-5039" data-mp="3le4sntzjjpis">Done.</p>
            <ul className="space-y-1" data-id="element-5040" data-mp="3hb847mnbkz6x">
              <li data-id="element-5041" data-mp="3ha5dbepzywr0">• Matched: 142 transactions ($42,150)</li>
              <li data-id="element-5042" data-mp="2e0aqzhxy4n8j">• Mismatched: 0</li>
              <li data-id="element-5043" data-mp="2dz9era571up8">• Missing invoices: 2 (Acme Corp, Globex)</li>
            </ul>
            <p data-id="element-5044" data-mp="2ixt3fib69kla">I drafted emails to Acme and Globex. Review drafts?</p>
          </div>,
      reactions: [{
        emoji: '📈',
        count: 2
      }]
    }
  }
}, {
  id: 'personal',
  icon: Home,
  name: 'Personal & Life',
  status: 'Live',
  channel: '#life-admin',
  desc: 'Travel, errands, reservations, and the loose ends of daily life.',
  scenario: {
    title: 'Hand off the personal logistics',
    user: {
      name: 'Maya Chen',
      avatar: 'https://i.pravatar.cc/100?img=44',
      time: '6:48 PM',
      message: <>
            <span className="text-[#EA580C] bg-[#EA580C]/10 px-1 rounded" data-id="element-5045" data-mp="247v9smuh42op">
              @Giga
            </span>{' '}
            book my Friday SF trip, find a dinner spot for 4 near the office,
            and reschedule my dentist for next week.
          </>
    },
    giga: {
      time: '6:49 PM',
      message: <div className="space-y-3" data-id="element-5046" data-mp="1gi0y48lw4k61">
            <p data-id="element-5047" data-mp="1914g1j51886p">Done.</p>
            <ul className="space-y-1" data-id="element-5048" data-mp="18zl0xvv4436i">
              <li data-id="element-5049" data-mp="18yi8hji55soh">• Flight: SFO Fri 9:10 AM, window seat — $382</li>
              <li data-id="element-5050" data-mp="142aztiyd232u">• Dinner: Rich Table, 7:30 PM for 4 (confirmed)</li>
              <li data-id="element-5051" data-mp="zzeomdb5h98x">• Dentist: moved to Tue 4:00 PM</li>
            </ul>
            <p data-id="element-5052" data-mp="v13c1uth0kn0">Added everything to your calendar. Want a car to SFO too?</p>
          </div>,
      reactions: [{
        emoji: '🙏',
        count: 2
      }]
    }
  }
}];
export function StartingPointSelector() {
  const [activeId, setActiveId] = useState(FUNCTIONS[0].id);
  const activeFunction = FUNCTIONS.find(f => f.id === activeId)!;
  return <section className="relative w-full bg-[#FAF8F4] py-16 md:py-28 overflow-hidden" data-id="element-5053" data-mp="gd080qyznj0u">
      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-5054" data-mp="cbeqgsbiegkb">
        {/* Section heading */}
        <div className="max-w-2xl mb-12 md:mb-16" data-id="element-5055" data-mp="7ah5y9311afn">
          <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-4 block" data-id="element-5056" data-mp="7dd3vxevjz43">
            Choose a starting point
          </span>
          <h2 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] tracking-tight leading-[1.05]" data-id="element-5057" data-mp="1qaf507v430sq">
            Start with the function that eats the most time.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-16 items-start" data-id="element-5058" data-mp="1h7nss5uew101">
          {/* LEFT: Selector List */}
          <div className="flex flex-col" data-id="element-5059" data-mp="1m9d0bbifdwjd">
            <div className="flex flex-col gap-1.5 mb-8" data-id="element-5060" data-mp="17hkz03fguj0n">
              {FUNCTIONS.map(func => {
              const isActive = activeId === func.id;
              const Icon = func.icon;
              return <button key={func.id} onClick={() => setActiveId(func.id)} aria-pressed={isActive} className={`group relative text-left w-full rounded-2xl transition-all duration-300 ${isActive ? 'bg-white shadow-[0_8px_24px_-12px_rgba(43,33,26,0.15)]' : 'bg-transparent hover:bg-white/50'}`} data-id="element-5061" data-mp="yiqxbyna32g8">
                    <div className="px-5 py-4 flex items-center gap-4" data-id="element-5062" data-mp="tj4ijkrsxe5b">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-300 ${isActive ? 'bg-[#EA580C] text-white' : 'bg-white text-neutral-400 group-hover:text-neutral-600'}`} data-id="element-5063" data-mp="9wt6zctgjqrp">
                        <Icon className="w-[18px] h-[18px]" data-id="element-5064" data-mp="jwa8di1ofihv" />
                      </div>
                      <span className={`text-[16px] font-bold transition-colors ${isActive ? 'text-[#2B211A]' : 'text-neutral-500 group-hover:text-[#2B211A]'}`} data-id="element-5065" data-mp="evwautxpbqy3">
                        {func.name}
                      </span>

                      <div className="ml-auto shrink-0" data-id="element-5066" data-mp="nwaza25ypm5t">
                        <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-colors ${isActive ? 'border-[#EA580C]' : 'border-neutral-300 group-hover:border-neutral-400'}`} data-id="element-5067" data-mp="12m9txuyb6fgj">
                          {isActive && <motion.div layoutId="sp-radio-indicator" className="w-2 h-2 rounded-full bg-[#EA580C]" data-id="element-5068" data-mp="1bmy9wv79ipw2" />}
                        </div>
                      </div>
                    </div>
                  </button>;
            })}
            </div>

            <WaitlistCTA className="inline-flex items-center justify-center bg-[#2B211A] hover:bg-[#1A1410] text-white px-7 py-3.5 rounded-full font-bold text-base transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 w-full sm:w-auto self-start" data-id="element-5069" data-mp="35v38hegoyoda">
              Get Started for Free
            </WaitlistCTA>
          </div>

          {/* RIGHT: Slack Mockup */}
          <div className="relative w-full" data-id="element-5070" data-mp="3olb6ji5jaomm">
            <div className="absolute inset-0 bg-[#EA580C]/[0.06] blur-[100px] rounded-full pointer-events-none" data-id="element-5071" data-mp="3ommbhlapu2fb" />

            <div className="relative bg-white rounded-[28px] border border-[#EAE4D9] shadow-[0_32px_64px_-20px_rgba(43,33,26,0.12)] overflow-hidden flex flex-col min-h-[460px]" data-id="element-5072" data-mp="3jkn7c975j1hb">
              {/* Slack-style channel header */}
              <div className="px-6 py-4 border-b border-[#EAE4D9] flex items-center gap-2.5" data-id="element-5073" data-mp="284q7hpxryx5b">
                <SlackIcon size={16} data-id="element-5074" data-mp="247u3vlincfxw" />
                <span className="text-sm font-bold text-[#2B211A]" data-id="element-5075" data-mp="jo85mwy99vps">
                  {(activeFunction as any).channel || '#leadership'}
                </span>
                <span className="text-xs text-neutral-400 font-medium" data-id="element-5076" data-mp="nmo01lwo26d8">
                  · Slack
                </span>
              </div>

              <div className="p-6 md:p-8 flex-1 flex flex-col" data-id="element-5077" data-mp="xf3h8h052qx2">
                <AnimatePresence mode="wait" data-id="element-5078" data-mp="12dwxuiaq2y1g">
                  <motion.div key={activeId} initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -8
                }} transition={{
                  duration: 0.3
                }} className="flex flex-col gap-7" data-id="element-5079" data-mp="12cvcnkzzsv60">
                    {/* User Message */}
                    <div className="flex gap-3.5" data-id="element-5080" data-mp="3d82hyc8llr6z">
                      <img src={activeFunction.scenario.user.avatar} alt="" className="w-9 h-9 rounded-lg shrink-0 object-cover" data-id="element-5081" data-mp="3i49exfj0nscs" />
                      <div className="flex flex-col gap-1" data-id="element-5082" data-mp="pb42b2y17xjv">
                        <div className="flex items-baseline gap-2" data-id="element-5083" data-mp="kc115tl6abh6">
                          <span className="font-bold text-[#2B211A] text-[14px]" data-id="element-5084" data-mp="kdu3iwor69jd">
                            {activeFunction.scenario.user.name}
                          </span>
                          <span className="text-xs text-neutral-400" data-id="element-5085" data-mp="tauth7rh0fox">
                            {activeFunction.scenario.user.time}
                          </span>
                        </div>
                        <div className="text-[14px] text-[#2B211A] leading-relaxed" data-id="element-5086" data-mp="13a3hzl3kh23w">
                          {activeFunction.scenario.user.message}
                        </div>
                      </div>
                    </div>

                    {/* Giga Reply */}
                    <div className="flex gap-3.5" data-id="element-5087" data-mp="1r0zdotb58hgf">
                      <div className="w-9 h-9 rounded-lg bg-[#EA580C] shrink-0 flex items-center justify-center" data-id="element-5088" data-mp="1m0uyr7pvvusb">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-4 h-4 object-contain [filter:brightness(0)_saturate(100%)_invert(100%)]" data-id="element-5089" data-mp="25mwjjpvcz6z6" />
                      </div>
                      <div className="flex flex-col gap-1 min-w-0" data-id="element-5090" data-mp="20n0oig4n2pvr">
                        <div className="flex items-baseline gap-2" data-id="element-5091" data-mp="20pd3qlj0im54">
                          <span className="font-bold text-[#2B211A] text-[14px]" data-id="element-5092" data-mp="1mukid6gfybqm">
                            Giga
                          </span>
                          <span className="px-1.5 py-0.5 rounded bg-neutral-100 text-[10px] font-bold text-neutral-500 uppercase tracking-wider" data-id="element-5093" data-mp="1cy7p29ot5zwe">
                            App
                          </span>
                          <span className="text-xs text-neutral-400" data-id="element-5094" data-mp="13wrf231wnzsp">
                            {activeFunction.scenario.giga.time}
                          </span>
                        </div>

                        <div className="bg-[#FAF8F4] border border-[#EAE4D9] rounded-2xl rounded-tl-sm p-4 text-[14px] text-[#2B211A] leading-relaxed mt-0.5" data-id="element-5095" data-mp="10w7r7mysxil8">
                          {activeFunction.scenario.giga.message}
                        </div>

                        <div className="flex gap-2 mt-1.5" data-id="element-5096" data-mp="r423wqjhvpcd">
                          {activeFunction.scenario.giga.reactions.map((reaction, i) => <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white border border-[#EAE4D9] text-xs font-medium text-neutral-600" data-id="element-5097" data-mp="i56uz6p2g15d" data-mpi={i}>
                                <span data-id="element-5098" data-mp="d4sldk7tgj58">{reaction.emoji}</span>
                                <span data-id="element-5099" data-mp="867n3qc4k9jf">{reaction.count}</span>
                              </div>)}
                          <div className="flex items-center justify-center w-6.5 h-6.5 rounded-full bg-white border border-[#EAE4D9] text-neutral-400 px-1.5 py-1" data-id="element-5100" data-mp="38plym4cmylz">
                            <Plus size={13} data-id="element-5101" data-mp="1xlxjwmnmfqbu" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}